import { Item, ProductCategoryFilter } from 'types';
import { capitalizeFirstLetter, combinePredicatesUsingOr } from 'utils/utils';

interface VisualizerCategoryFilter extends ProductCategoryFilter {
  name: string;
  products?: Item[];
}

interface FilterState {
  colorFilters: ColorFilter[];
  patternFilters: string[];
  categoryFilters: VisualizerCategoryFilter[];
  activeCategory: VisualizerCategoryFilter;
}

export const colorToHexCode = {
  Black: '#000000',
  Gray: '#888888',
  Grey: '#888888',
  White: '#efefef',
  Yellow: '#f6eaab',
  Tan: '#d2b48c',
  Orange: '#f9905b',
  Red: '#c4383f',
  Pink: '#ffc0cb',
  Purple: '#8559c8',
  Blue: '#6779de',
  Green: '#416630',
  Brown: '#8f4c1c',
  Burgundy: '#800020',
} as const;

export type ColorFilter = keyof typeof colorToHexCode;

export type HexCode = (typeof colorToHexCode)[ColorFilter];

const normalizeColor = (color: string): string => {
  return capitalizeFirstLetter(color);
};

const visualizer = {
  isColorFilter(color: string): color is ColorFilter {
    return normalizeColor(color) in colorToHexCode;
  },

  getHexCodeForFilter(color: ColorFilter): HexCode {
    return colorToHexCode[normalizeColor(color) as ColorFilter];
  },

  /**
   * Retrieves a list of all color filters in the order they are defined.
   * @returns An array of `ColorFilter` values.
   */
  getOrderedColorFilters(): ColorFilter[] {
    return Object.keys(colorToHexCode) as ColorFilter[];
  },

  /**
   * Orders product colors based on the predefined list of color filters and appends any additional colors not in the filter list.
   * @param productColors - An array of product colors as strings.
   * @returns A combined array of colors, starting with those present in both the productColors and the color filters, followed by additional product colors not in the color filters.
   */
  getOrderedProductColors(productColors: string[]): string[] {
    const orderedColorFilters = this.getOrderedColorFilters();
    const productColorsSet = new Set(productColors);

    const orderedMutualColors = orderedColorFilters.filter((color) => productColorsSet.has(color)) as string[];

    const orderedMutualColorsSet = new Set(orderedMutualColors);
    // Find colors in productColors not included in getOrderedColorFilters() and append them
    const additionalColors = Array.from(productColorsSet).filter((color) => !orderedMutualColorsSet.has(color));

    // Return the combined array with additional colors appended
    return [...orderedMutualColors, ...additionalColors];
  },

  getDisplayedProducts(products: Item[], colorFilters: string[], categoryFilters: VisualizerCategoryFilter[], patternFilters: string[]) {
    return products.filter((product) => {
      // Keep products that match category filters.
      if (categoryFilters.length > 0) {
        const combinedPredicates = combinePredicatesUsingOr(categoryFilters.map((filter) => filter.value));

        if (product.category === 'Tie') {
          // Combined category filtering will not work with ties
          // with current tie filters, so only filter by display names.
          if (!([product.display_name!].filter(combinedPredicates).length > 0)) {
            return false;
          }
        } else {
          if (!([product.display_name!, product.category!].filter(combinedPredicates).length > 0)) {
            return false;
          }
        }
      }

      // Keep products that match pattern filters.
      if (patternFilters.length > 0) {
        if (!patternFilters.includes(product.pattern!)) {
          return false;
        }
      }

      // Keep products that match color filters.
      if (colorFilters.length > 0) {
        if (!colorFilters.includes(product.color!)) {
          return false;
        }
      }

      return true;
    });
  },

  toggleColorFilters(color: ColorFilter) {
    return (state: FilterState) => {
      // Either add or remove the color.
      const colorFilters = state.colorFilters.includes(color)
        ? state.colorFilters.filter((colorFilter) => colorFilter !== color)
        : state.colorFilters.concat(color);

      const displayedProducts = visualizer.getDisplayedProducts(
        state.activeCategory.products!,
        colorFilters,
        state.categoryFilters,
        state.patternFilters,
      );

      return { colorFilters, displayedProducts };
    };
  },

  toggleCategoryFilters(categoryFilter: VisualizerCategoryFilter) {
    return (state: FilterState) => {
      // Either add or remove the category.
      const categoryFilters = state.categoryFilters.includes(categoryFilter)
        ? state.categoryFilters.filter((filter) => filter.displayName !== categoryFilter.displayName)
        : state.categoryFilters.concat(categoryFilter);

      const displayedProducts = visualizer.getDisplayedProducts(
        state.activeCategory.products!,
        state.colorFilters,
        categoryFilters,
        state.patternFilters,
      );

      return { categoryFilters, displayedProducts };
    };
  },

  togglePatternFilters(patternFilter: string) {
    return (state: FilterState) => {
      // Either add or remove the pattern.
      const patternFilters = state.patternFilters.includes(patternFilter)
        ? state.patternFilters.filter((filter) => filter !== patternFilter)
        : state.patternFilters.concat(patternFilter);

      const displayedProducts = visualizer.getDisplayedProducts(
        state.activeCategory.products!,
        state.colorFilters,
        state.categoryFilters,
        patternFilters,
      );

      return { patternFilters, displayedProducts };
    };
  },
};

export default visualizer;
